import React, { useState, useEffect, useMemo } from 'react'; // useMemo kept for client-side mode
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import paymentsService from '../services/paymentsService';
import styles from '../assets/paymentsIndex.module.css';

const PaymentsIndex = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();

  // Server-side pagination state
  const [paymentsData, setPaymentsData] = useState([]); // Changed from null to array for server-side data
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [apiFailed, setApiFailed] = useState(false);
  const [useMockData, setUseMockData] = useState(false);
  const [useServerSide, setUseServerSide] = useState(true); // NEW: Toggle server-side pagination
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [tableSearch, setTableSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [draw, setDraw] = useState(1); // NEW: For DataTables protocol
  const [sortField, setSortField] = useState('due_date'); // NEW: Server-side sort field
  const [sortDirection, setSortDirection] = useState('desc'); // NEW: Server-side sort direction

  // Statistics data (fetched separately for overview cards)
  const [statistics, setStatistics] = useState({
    all: 0,
    overdue: 0,
    paid: 0,
    to_pay: 0,
    legal: 0,
    payment_plan: 0,
    notice_to_quit: 0,
    totalPriceOfOccupiedRooms: 0,
    totalPriceOfVacantRooms: 0
  });

  const [columnVisibility, setColumnVisibility] = useState({
    firstName: true,
    lastName: true,
    mobile: true,
    fee: true,
    room: true,
    dueDate: true,
    movedInDate: true,
    oa: true,
    paymentStatus: true,
    rentStatus: true,
    property: true,
    notes: true,
    bankAccount: true,
    actions: true
  });
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedRows, setSelectedRows] = useState([]);

  // Map frontend sort field to backend column index (matching Users.jsx)
  const getSortColumnIndex = (field) => {
    const mapping = {
      "id": 0,
      "first_name": 1,
      "last_name": 2,
      "mobile": 3,
      "fee": 4,
      "room": 5,
      "due_date": 6,
      "moved_in_date": 7,
      "oa_status": 8,
      "payment_status": 9,
      "rent_status": 10,
      "property": 11,
    };
    return mapping[field] || 6; // Default to due_date
  };

  // NEW: Server-side pagination fetch
  const fetchPaymentsServerSide = async () => {
    try {
      setLoading(true);
      console.log("🔍 [FRONTEND] Fetching payments with server-side pagination");

      const startIndex = (currentPage - 1) * entriesPerPage;

      const datatableParams = {
        draw: draw,
        start: startIndex,
        length: entriesPerPage,
        search: { value: debouncedSearch },
        order: [{ column: getSortColumnIndex(sortField), dir: sortDirection }],
        filter: activeFilter // 'all', 'overdue', 'paid', 'to_pay'
      };

      console.log("📊 [FRONTEND] DataTables params:", datatableParams);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/datatable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify(datatableParams)
      });

      const data = await response.json();
      console.log("📊 [FRONTEND] DataTables response:", data);

      if (data.success) {
        // Ensure data.data is an array
        const paymentsArray = Array.isArray(data.data) ? data.data : [];
        setPaymentsData(paymentsArray);
        setTotalRecords(data.recordsFiltered || 0);
        setTotalPages(Math.ceil((data.recordsFiltered || 0) / entriesPerPage));
        setDraw(data.draw);
        setApiFailed(false);
      } else {
        setPaymentsData([]); // Ensure empty array on error
        setApiFailed(true);
        toast.error("Failed to load payments");
      }
    } catch (error) {
      console.error("❌ [FRONTEND] Error fetching payments:", error);
      setPaymentsData([]);
      setTotalRecords(0);
      setApiFailed(true);
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  // OLD: Client-side pagination fetch (kept for backwards compatibility)
  const loadPaymentsData = async () => {
    try {
      setLoading(true);
      let response;

      // Use different service method based on the route
      if (location.pathname === '/payments/without-due-date') {
        response = await paymentsService.getPaymentsWithoutDueDate();
      } else {
        response = await paymentsService.getActivePayments();
      }

      if (response.success) {
        // For client-side mode, set the nested data structure
        setStatistics({
          all: response.data.payments?.length || 0,
          overdue: response.data.paymentsNotPaid?.length || 0,
          paid: response.data.paymentsPaid?.length || 0,
          to_pay: response.data.paymentsToPay?.length || 0,
          legal: response.data.paymentsLegal?.length || 0,
          payment_plan: response.data.paymentsPlan?.length || 0,
          notice_to_quit: response.data.paymentsNoticeToQuit?.length || 0,
          totalPriceOfOccupiedRooms: response.data.totalPriceOfOccupiedRooms || 0,
          totalPriceOfVacantRooms: response.data.totalPriceOfVacantRooms || 0
        });
        setPaymentsData(response.data); // Keep original nested structure for client-side
        setApiFailed(false);
      } else {
        setApiFailed(true);
      }
    } catch (error) {
      console.error('Error loading payments data:', error);
      setApiFailed(true);
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics for overview cards (separate from pagination)
  const fetchStatistics = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/active`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`
        }
      });

      const data = await response.json();
      if (data.success && data.data) {
        // IMPORTANT: Use guardian counts for statistics (not payment counts)
        // Laravel counts unique guardians (rents), not individual payments
        const guardianCounts = data.data.guardianCounts || {};
        console.log("🔍 [FRONTEND] Guardian counts from API:", guardianCounts);
        console.log("🔍 [FRONTEND] Payment counts (for comparison):", {
          all: data.data.payments?.length || 0,
          overdue: data.data.paymentsNotPaid?.length || 0,
          paid: data.data.paymentsPaid?.length || 0,
          to_pay: data.data.paymentsToPay?.length || 0,
        });
        
        const stats = {
          all: guardianCounts.all || data.data.payments?.length || 0,
          overdue: guardianCounts.overdue || data.data.paymentsNotPaid?.length || 0,
          paid: guardianCounts.paid || data.data.paymentsPaid?.length || 0,
          to_pay: guardianCounts.to_pay || data.data.paymentsToPay?.length || 0,
          legal: guardianCounts.legal || data.data.paymentsLegal?.length || 0,
          payment_plan: guardianCounts.plan || data.data.paymentsPlan?.length || 0,
          notice_to_quit: guardianCounts.notice_to_quit || data.data.paymentsNoticeToQuit?.length || 0,
          totalPriceOfOccupiedRooms: data.data.totalPriceOfOccupiedRooms || 0,
          totalPriceOfVacantRooms: data.data.totalPriceOfVacantRooms || 0
        };
        
        console.log("🔍 [FRONTEND] Setting statistics:", stats);
        setStatistics(stats);
      }
    } catch (error) {
      console.error("Error fetching statistics:", error);
    }
  };

  // Search only triggers on Enter key press, not on every keystroke
  // Removed automatic debounce - search now only works on Enter key

  // Fetch payments when dependencies change
  useEffect(() => {
    if (useServerSide) {
      fetchPaymentsServerSide();
    } else {
      loadPaymentsData();
    }
  }, [activeFilter, currentPage, entriesPerPage, debouncedSearch, sortField, sortDirection, useServerSide]);

  // Fetch statistics once on mount (for overview cards)
  useEffect(() => {
    if (useServerSide) {
      fetchStatistics();
    }
  }, [useServerSide]);

  // Refresh data when navigating back to /payments page (e.g., after marking payment as paid)
  // This ensures the overdue list updates after marking payments as paid
  useEffect(() => {
    if (!useServerSide || location.pathname !== '/payments') return;

    // Refresh when location changes to /payments (user navigates back)
    // Small delay to ensure backend has finished processing payment status synchronization
    const timeoutId = setTimeout(() => {
      console.log("🔄 [PAYMENTS] Refreshing data after navigation to /payments...");
      fetchPaymentsServerSide();
      fetchStatistics();
    }, 1000); // 1 second delay to allow backend synchronization to complete

    return () => {
      clearTimeout(timeoutId);
    };
  }, [location.pathname, useServerSide]);

  // CLIENT-SIDE ONLY: Get filtered payments (kept for backwards compatibility)
  const getFilteredPayments = () => {
    if (useServerSide) {
      // In server-side mode, paymentsData is already the filtered array
      // Ensure it's always an array
      if (Array.isArray(paymentsData)) {
        return paymentsData;
      }
      console.warn('⚠️ [PAYMENTS] getFilteredPayments: paymentsData is not an array in server-side mode');
      return [];
    }

    // Client-side filtering (old behavior)
    // Ensure paymentsData is an object (not an array) for client-side mode
    if (!paymentsData || typeof paymentsData !== 'object' || Array.isArray(paymentsData)) {
      console.warn('⚠️ [PAYMENTS] getFilteredPayments: paymentsData is not an object (client-side mode expects object with nested arrays)');
      return [];
    }

    let filtered = [];
    switch (activeFilter) {
      case 'all':
        filtered = paymentsData.payments || [];
        break;
      case 'overdue':
        filtered = paymentsData.paymentsNotPaid || [];
        break;
      case 'paid':
        filtered = paymentsData.paymentsPaid || [];
        break;
      case 'to_pay':
        filtered = paymentsData.paymentsToPay || [];
        break;
      case 'legal':
        filtered = paymentsData.paymentsLegal || [];
        break;
      case 'payment_plan':
        filtered = paymentsData.paymentsPlan || [];
        break;
      case 'notice_to_quit':
        filtered = paymentsData.paymentsNoticeToQuit || [];
        break;
      case 'without_due_date':
        filtered = paymentsData.paymentsWithoutDueDate || paymentsData.payments || [];
        break;
      default:
        filtered = paymentsData.payments || [];
    }

    // Apply search filter (client-side only)
    if (tableSearch) {
      filtered = filtered.filter(payment =>
        payment.first_name?.toLowerCase().includes(tableSearch.toLowerCase()) ||
        payment.last_name?.toLowerCase().includes(tableSearch.toLowerCase()) ||
        payment.mobile?.toLowerCase().includes(tableSearch.toLowerCase()) ||
        payment.room?.toLowerCase().includes(tableSearch.toLowerCase()) ||
        payment.property?.toLowerCase().includes(tableSearch.toLowerCase()) ||
        payment.bank_account?.toLowerCase().includes(tableSearch.toLowerCase()) ||
        payment.notes?.toLowerCase().includes(tableSearch.toLowerCase())
      );
    }

    // Apply sorting (client-side only)
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle different data types
        if (sortConfig.key === 'fee') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        } else if (sortConfig.key === 'due_date' || sortConfig.key === 'moved_in_date') {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        } else {
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  };

  // Calculate paginated payments (works for both server and client-side)
  const paginatedPayments = useMemo(() => {
    if (useServerSide) {
      // Server-side: paymentsData is already paginated
      // Ensure it's always an array
      if (Array.isArray(paymentsData)) {
        return paymentsData;
      }
      // If paymentsData is not an array, return empty array
      console.warn('⚠️ [PAYMENTS] paymentsData is not an array in server-side mode:', typeof paymentsData, paymentsData);
      return [];
    }

    // Client-side: apply pagination to filtered data
    const filtered = getFilteredPayments();
    // Ensure filtered is an array
    if (!Array.isArray(filtered)) {
      console.warn('⚠️ [PAYMENTS] getFilteredPayments did not return an array:', typeof filtered, filtered);
      return [];
    }
    const total = filtered.length;
    const pages = Math.ceil(total / entriesPerPage);

    const validPage = Math.min(currentPage, pages || 1);
    const startIndex = (validPage - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    return filtered.slice(startIndex, endIndex);
  }, [paymentsData, activeFilter, tableSearch, entriesPerPage, currentPage, sortConfig, useServerSide]);

  // CLIENT-SIDE ONLY: Update pagination state when data changes
  useEffect(() => {
    if (!useServerSide) {
      const filtered = getFilteredPayments();
      const total = filtered.length;
      const pages = Math.ceil(total / entriesPerPage);

      setTotalPages(pages);
      setTotalRecords(total);

      // Ensure current page is valid
      const validPage = Math.min(currentPage, pages || 1);
      if (validPage !== currentPage) {
        setCurrentPage(validPage);
      }
    }
  }, [paymentsData, activeFilter, tableSearch, entriesPerPage, currentPage, sortConfig, useServerSide]);

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const toggleMockData = () => {
    setUseMockData(!useMockData);
  };

  // Pagination functions
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToPreviousPage = () => {
    goToPage(currentPage - 1);
  };

  const goToNextPage = () => {
    goToPage(currentPage + 1);
  };

  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handleColumnVisibility = () => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  const toggleColumnVisibility = (columnKey) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Handle checkbox clicks to prevent dropdown from closing
  const handleCheckboxClick = (e) => {
    e.stopPropagation();
  };

  // Sorting functionality (works for both server and client-side)
  const handleSort = (key) => {
    if (useServerSide) {
      // Server-side sorting
      if (sortField === key) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(key);
        setSortDirection('asc');
      }
    } else {
      // Client-side sorting
      let direction = 'asc';
      if (sortConfig.key === key && sortConfig.direction === 'asc') {
        direction = 'desc';
      }
      setSortConfig({ key, direction });
    }
  };

  const getSortIcon = (columnKey) => {
    if (useServerSide) {
      // Server-side sort icon
      if (sortField !== columnKey) {
        return '↕'; // Default sort icon
      }
      return sortDirection === 'asc' ? '↑' : '↓';
    } else {
      // Client-side sort icon
      if (sortConfig.key !== columnKey) {
        return '↕'; // Default sort icon
      }
      return sortConfig.direction === 'asc' ? '↑' : '↓';
    }
  };

  // Row selection functionality
  const handleRowClick = (payment, event) => {
    // Don't select row if clicking on action buttons
    if (event.target.closest('.actionButtonsGroup')) {
      return;
    }

    if (selectedRows.includes(payment.id)) {
      setSelectedRows(selectedRows.filter(id => id !== payment.id));
    } else {
      setSelectedRows([...selectedRows, payment.id]);
    }
  };

  const handleSelectAll = () => {
    const allIds = paginatedPayments.map(payment => payment.id);
    if (selectedRows.length === allIds.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(allIds);
    }
  };

  // Click outside handler for column visibility dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the dropdown and button
      const dropdown = event.target.closest(`.${styles.columnVisibilityDropdown}`);
      const button = event.target.closest(`.${styles.visibilityBtn}`);
      const container = event.target.closest(`.${styles.visibilityBtnContainer}`);
      
      if (!dropdown && !button && !container) {
        setShowColumnVisibility(false);
      }
    };

    if (showColumnVisibility) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnVisibility]);

  const handleExportCSV = async () => {
    try {
      const payments = getFilteredPayments();
      
      // Create CSV content
      const headers = [
        'First Name',
        'Last Name', 
        'Mobile',
        'Fee',
        'Room',
        'Due Date',
        'Moved In Date',
        'O/A',
        'Payment Status',
        'Rent Status',
        'Property',
        'Notes',
        'Bank Account'
      ];

      const csvContent = [
        headers.join(','),
        ...payments.map(payment => [
          `"${payment.first_name || ''}"`,
          `"${payment.last_name || ''}"`,
          `"${payment.mobile || ''}"`,
          `"${formatCurrency(payment.fee)}"`,
          `"${payment.room || ''}"`,
          `"${formatDate(payment.due_date)}"`,
          `"${formatDate(payment.moved_in_date)}"`,
          `"${formatCurrency(payment.outstanding_amount !== undefined ? payment.outstanding_amount : (typeof payment.oa_status === 'number' ? payment.oa_status : 0))}"`,
          `"${payment.payment_status || ''}"`,
          `"${payment.rent_status || ''}"`,
          `"${payment.property || ''}"`,
          `"${payment.notes || ''}"`,
          `"${payment.bank_account || ''}"`
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `payments_${activeFilter}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('Export CSV error:', error);
      toast.error('Failed to export CSV');
    }
  };

  const handleExportPDF = async () => {
    try {
      console.log("📄 [PAYMENTS] Starting PDF export...");
      const payments = getFilteredPayments();
      
      // Check if we have data to export
      if (!payments || payments.length === 0) {
        toast.warning("No data available to export. Please check your filters or try refreshing the page.");
        return;
      }
      
      // Dynamic import for jsPDF and autoTable with proper error handling
      console.log("📦 [PAYMENTS] Importing jsPDF...");
      const jsPDFModule = await import("jspdf");
      
      // Handle different jsPDF export formats
      let jsPDF;
      if (jsPDFModule.default) {
        // Default export (most common)
        jsPDF = jsPDFModule.default;
      } else if (jsPDFModule.jsPDF) {
        // Named export
        jsPDF = jsPDFModule.jsPDF;
      } else if (typeof jsPDFModule === "function") {
        // Direct function export
        jsPDF = jsPDFModule;
      } else {
        throw new Error("jsPDF library not found in module");
      }
      
      console.log("📦 [PAYMENTS] jsPDF imported successfully", { 
        isFunction: typeof jsPDF === "function",
        hasDefault: !!jsPDFModule.default 
      });
      
      if (typeof jsPDF !== "function") {
        throw new Error("jsPDF is not a constructor function");
      }
      
      // Create PDF document first (before importing autoTable)
      let doc;
      try {
        // Try newer API first
        doc = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: "a4",
        });
      } catch (e) {
        console.log("New API failed, trying legacy API:", e);
        // Fallback to legacy API
        doc = new jsPDF("landscape", "mm", "a4");
      }
      
      // Verify doc is a valid jsPDF instance
      if (!doc || typeof doc.setFontSize !== "function") {
        throw new Error("Failed to create valid jsPDF document instance");
      }
      
      console.log("📄 [PAYMENTS] jsPDF document created successfully");
      
      // Import and attach autoTable plugin
      console.log("📦 [PAYMENTS] Importing jspdf-autotable...");
      try {
        const autoTableModule = await import("jspdf-autotable");
        
        // Some versions require explicit attachment
        if (autoTableModule.default && typeof autoTableModule.default === "function") {
          autoTableModule.default(jsPDF);
        }
        
        // Verify autoTable is now available
        if (typeof doc.autoTable !== "function") {
          console.warn("⚠️ [PAYMENTS] autoTable not found after import, will use fallback");
        } else {
          console.log("✅ [PAYMENTS] autoTable plugin loaded successfully");
        }
      } catch (autoTableError) {
        console.warn("⚠️ [PAYMENTS] Failed to load autoTable plugin:", autoTableError);
        // Continue without autoTable - we'll use fallback
      }
      
      // Add title
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("Guardian Payments Report", 20, 20);
      
      // Add metadata
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Generated on: ${new Date().toLocaleDateString("en-GB")}`, 20, 30);
      doc.text(`Filter: ${activeFilter.toUpperCase()}`, 20, 37);
      doc.text(`Total Records: ${payments.length}`, 20, 44);
      
      // Helper function to clean and sanitize text for PDF
      const cleanText = (value) => {
        if (value === null || value === undefined) return "";
        
        // Convert to string
        let cleaned = String(value);
        
        // Remove HTML tags first
        cleaned = cleaned.replace(/<[^>]*>/g, "");
        
        // Handle malformed HTML entity patterns (like &Z&o&f&i&a&)
        // This pattern appears when each character is wrapped in &...&
        if (cleaned.includes("&")) {
          // Step 1: Decode standard HTML entities first
          const entityMap = {
            "&amp;": "&",
            "&lt;": "<",
            "&gt;": ">",
            "&quot;": '"',
            "&#39;": "'",
            "&nbsp;": " ",
            "&#160;": " ",
          };
          
          Object.keys(entityMap).forEach((entity) => {
            cleaned = cleaned.replace(new RegExp(entity, "gi"), entityMap[entity]);
          });
          
          // Step 2: Handle malformed pattern where single characters are wrapped: &X&Y&Z&
          // Extract all single alphanumeric characters between & symbols
          // This handles patterns like &Z&o&f&i&a&
          cleaned = cleaned.replace(/&([a-zA-Z0-9])&/g, "$1");
          
          // Step 3: Handle spaces with entities: & & & becomes a single space
          cleaned = cleaned.replace(/&\s*&\s*/g, " "); // & & becomes space
          cleaned = cleaned.replace(/&\s*&\s*&\s*/g, " "); // & & & becomes space
          
          // Step 4: Remove any remaining malformed entity patterns (like &Z; or &unknown;)
          cleaned = cleaned.replace(/&[a-zA-Z0-9#]+;/g, "");
          
          // Step 5: Remove incomplete entities (like &Z without semicolon)
          cleaned = cleaned.replace(/&[a-zA-Z0-9#]+(?![;\s])/g, "");
          
          // Step 6: Remove any remaining isolated & symbols
          cleaned = cleaned.replace(/^&+|&+$/g, ""); // Remove leading/trailing &
          cleaned = cleaned.replace(/\s*&\s*/g, " "); // Replace isolated & with space
          cleaned = cleaned.replace(/&+/g, ""); // Remove any remaining consecutive &
        }
        
        // Final cleanup: remove multiple spaces and trim
        cleaned = cleaned.replace(/\s+/g, " ").trim();
        
        return cleaned;
      };
      
      // Helper function to extract text from status badges (remove HTML tags and decode entities)
      const extractStatusText = (status) => {
        if (!status) return "";
        // If status is already a plain string, return it
        if (typeof status === 'string' && !status.includes('<') && !status.includes('&')) {
          return status;
        }
        return cleanText(status);
      };
      
      // Helper function to get raw text value (prioritize raw fields over HTML)
      const getRawValue = (payment, field, htmlField = null) => {
        // Try to get raw value first (e.g., payment.first_name_raw)
        const rawField = htmlField ? payment[htmlField] : payment[field + '_raw'];
        if (rawField !== undefined && rawField !== null) {
          return String(rawField);
        }
        // Fallback to regular field
        const value = payment[field];
        if (value === null || value === undefined) return "";
        return cleanText(value);
      };
      
      // Prepare table data with simplified columns matching the image format
      // Format: First Name | Last Name | Mobile | Fee | Room | Due Date | Status | Property
      const tableData = payments.map((payment) => {
        // Get raw text values, avoiding HTML-formatted data
        const firstName = getRawValue(payment, 'first_name');
        const lastName = getRawValue(payment, 'last_name');
        const mobile = getRawValue(payment, 'mobile');
        const room = getRawValue(payment, 'room');
        const property = getRawValue(payment, 'property');
        
        // Get payment status (use payment_status, fallback to "Paid" if not available)
        const paymentStatus = extractStatusText(payment.payment_status) || "Paid";
        
        // Format fee as currency
        const fee = formatCurrency(payment.fee);
        
        // Format due date
        const dueDate = formatDate(payment.due_date);
        
        return [
          firstName || "",
          lastName || "",
          mobile || "",
          fee || "£0.00",
          room || "",
          dueDate || "",
          paymentStatus,
          property || "",
        ];
      });
      
      // Generate table using autoTable with simplified format
      // Ensure autoTable is available by checking window object
      if (typeof doc.autoTable === "function") {
        console.log("✅ [PAYMENTS] Using autoTable for PDF generation");
        try {
          doc.autoTable({
            startY: 50,
            head: [
              [
                "First Name",
                "Last Name",
                "Mobile",
                "Fee",
                "Room",
                "Due Date",
                "Status",
                "Property",
              ],
            ],
            body: tableData,
            theme: "striped",
            headStyles: {
              fillColor: [66, 139, 202],
              textColor: 255,
              fontStyle: "bold",
              fontSize: 10,
            },
            bodyStyles: {
              fontSize: 9,
              textColor: [0, 0, 0],
            },
            columnStyles: {
              0: { cellWidth: 30 }, // First Name
              1: { cellWidth: 30 }, // Last Name
              2: { cellWidth: 30 }, // Mobile
              3: { cellWidth: 25 }, // Fee
              4: { cellWidth: 20 }, // Room
              5: { cellWidth: 30 }, // Due Date
              6: { cellWidth: 25 }, // Status
              7: { cellWidth: 50 }, // Property
            },
            styles: {
              overflow: "linebreak",
              cellPadding: 3,
              halign: "left",
            },
            margin: { top: 50, left: 20, right: 20 },
            didParseCell: function (data) {
              // Ensure all cell content is plain text (no HTML)
              if (data.cell && data.cell.text) {
                data.cell.text = cleanText(data.cell.text);
              }
            },
          });
          console.log("✅ [PAYMENTS] autoTable generated successfully");
        } catch (autoTableError) {
          console.error("❌ [PAYMENTS] autoTable error:", autoTableError);
          console.log("⚠️ [PAYMENTS] Falling back to manual table generation");
          
          // Fallback: Create simple table manually
          const startY = 50;
          const lineHeight = 7;
          const columnWidths = [30, 30, 30, 25, 20, 30, 25, 50];
          const columnPositions = [20];
          for (let i = 1; i < columnWidths.length; i++) {
            columnPositions.push(columnPositions[i - 1] + columnWidths[i - 1]);
          }
          
          // Draw header
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          const headers = ["First Name", "Last Name", "Mobile", "Fee", "Room", "Due Date", "Status", "Property"];
          headers.forEach((header, colIndex) => {
            doc.text(header, columnPositions[colIndex], startY);
          });
          
          // Draw rows
          doc.setFontSize(9);
          doc.setFont("helvetica", "normal");
          let currentY = startY + lineHeight;
          
          tableData.forEach((row, rowIndex) => {
            // Check if we need a new page
            if (currentY > 190) {
              doc.addPage();
              currentY = 20;
            }
            
            row.forEach((cell, colIndex) => {
              const cellText = String(cell || "").substring(0, 20); // Limit cell width
              doc.text(cellText, columnPositions[colIndex], currentY);
            });
            
            currentY += lineHeight;
          });
        }
      } else {
        // Fallback: Create simple table manually without autoTable
        console.warn("⚠️ [PAYMENTS] autoTable not available, using manual table generation");
        
        const startY = 50;
        const lineHeight = 7;
        const columnWidths = [30, 30, 30, 25, 20, 30, 25, 50];
        const columnPositions = [20];
        for (let i = 1; i < columnWidths.length; i++) {
          columnPositions.push(columnPositions[i - 1] + columnWidths[i - 1]);
        }
        
        // Draw header
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        const headers = ["First Name", "Last Name", "Mobile", "Fee", "Room", "Due Date", "Status", "Property"];
        headers.forEach((header, colIndex) => {
          doc.text(header, columnPositions[colIndex], startY);
        });
        
        // Draw rows
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        let currentY = startY + lineHeight;
        
        tableData.forEach((row) => {
          // Check if we need a new page
          if (currentY > 190) {
            doc.addPage();
            currentY = 20;
          }
          
          row.forEach((cell, colIndex) => {
            const cellText = String(cell || "").substring(0, 20); // Limit cell width
            doc.text(cellText, columnPositions[colIndex], currentY);
          });
          
          currentY += lineHeight;
        });
      }
      
      // Save PDF
      const filename = `payments_${activeFilter}_${new Date().toISOString().split("T")[0]}.pdf`;
      doc.save(filename);
      
      console.log(`✅ [PAYMENTS] PDF exported successfully: ${filename}`);
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error("❌ [PAYMENTS] Error exporting PDF:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });
      toast.error(`Failed to export PDF: ${error.message || "Unknown error"}`);
    }
  };

  const handlePrintTable = () => {
    try {
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      
      // Get the table data
      const tableData = paginatedPayments.map(payment => ({
        firstName: payment.first_name,
        lastName: payment.last_name,
        mobile: payment.mobile,
        fee: formatCurrency(payment.fee),
        room: payment.room,
        dueDate: formatDate(payment.due_date),
        movedInDate: formatDate(payment.moved_in_date),
        oa: formatCurrency(payment.outstanding_amount !== undefined ? payment.outstanding_amount : (typeof payment.oa_status === 'number' ? payment.oa_status : 0)),
        paymentStatus: payment.payment_status,
        rentStatus: payment.rent_status,
        property: payment.property,
        notes: payment.notes || '-',
        bankAccount: payment.bank_account
      }));

      // Create print-friendly HTML
      const printHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Guardian Payments - Print</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; text-align: center; margin-bottom: 30px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
            th { background-color: #f8f9fa; font-weight: bold; }
            .header { margin-bottom: 20px; }
            .date { text-align: right; color: #666; font-size: 12px; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Guardian Payments Report</h1>
            <div class="date">Generated on: ${new Date().toLocaleDateString('en-GB')}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Mobile</th>
                <th>Fee</th>
                <th>Room</th>
                <th>Due Date</th>
                <th>Moved In Date</th>
                <th>O/A</th>
                <th>Payment Status</th>
                <th>Rent Status</th>
                <th>Property</th>
                <th>Notes</th>
                <th>Bank Account</th>
              </tr>
            </thead>
            <tbody>
              ${tableData.map(payment => `
                <tr>
                  <td>${payment.firstName}</td>
                  <td>${payment.lastName}</td>
                  <td>${payment.mobile}</td>
                  <td>${payment.fee}</td>
                  <td>${payment.room}</td>
                  <td>${payment.dueDate}</td>
                  <td>${payment.movedInDate}</td>
                  <td>${payment.oa}</td>
                  <td>${payment.paymentStatus}</td>
                  <td>${payment.rentStatus}</td>
                  <td>${payment.property}</td>
                  <td>${payment.notes}</td>
                  <td>${payment.bankAccount}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="no-print">
            <button onclick="window.print()">Print</button>
            <button onclick="window.close()">Close</button>
          </div>
        </body>
        </html>
      `;

      printWindow.document.write(printHTML);
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = () => {
        printWindow.print();
      };
      
      toast.success('Print window opened');
    } catch (error) {
      console.error('Error printing table:', error);
      toast.error('Failed to print table');
    }
  };

  // Handle send reminder functionality - exact Laravel implementation
  const handleSendReminder = async (payment) => {
    const guardianName = `${payment.first_name} ${payment.last_name}`;
    const confirmMessage = `Are you sure you want to send a payment reminder to ${guardianName}?`;
    
    if (window.confirm(confirmMessage)) {
      try {
        const response = await paymentsService.sendPaymentReminder(payment.id);
        
        if (response.success) {
          toast.success("You sent the payment reminder!");
        } else {
          toast.error(response.message || "Failed to send payment reminder");
        }
      } catch (error) {
        console.error('Error sending payment reminder:', error);
        toast.error("Failed to send payment reminder");
      }
    }
  };

  // Helper function to check if payment is overdue (Laravel: Payment::isOverdue())
  const isPaymentOverdue = (payment) => {
    if (!payment.due_date) {
      return false;
    }
    // Laravel: Check if payment status is not paid (0) AND due_date is in the past
    // Get the original status (use status or payment_status field)
    const status = payment.status !== undefined ? payment.status : payment.payment_status;
    const statusLower = typeof status === 'string' ? status.toLowerCase() : String(status);

    // Laravel only shows overdue for status = 0 (Not paid), not other statuses
    // Check if status is "not paid" (status code 0 or text "not paid")
    const isNotPaidStatus = status === 0 || status === '0' || statusLower === 'not paid';
    if (!isNotPaidStatus) {
      return false; // Only status 0 (Not paid) can be overdue
    }

    const dueDate = new Date(payment.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  // Helper function to check if payment is not paid (matching Laravel logic)
  const isPaymentNotPaid = (payment) => {
    // Laravel checks for status 0 (not_paid)
    return payment.payment_status === 'Overdue' || payment.payment_status === 'Not paid';
  };

  // Helper function to check if payment is paid (matching Laravel logic)
  const isPaymentPaid = (payment) => {
    return payment.payment_status === 'Paid';
  };

  // Handle remove payment functionality - matching Laravel delete with confirmation
  const handleRemovePayment = async (payment) => {
    const guardianName = `${payment.first_name} ${payment.last_name}`;
    const confirmMessage = `Are you sure you want to remove the payment for ${guardianName} (${formatCurrency(payment.fee)})?`;

    if (window.confirm(confirmMessage)) {
      try {
        const response = await paymentsService.deletePayment(payment.id);

        if (response.success) {
          toast.success("Payment has been deleted");
          // Reload the payments data to refresh the table
          loadPaymentsData();
        } else {
          toast.error(response.message || "Failed to delete payment");
        }
      } catch (error) {
        console.error('Error deleting payment:', error);
        toast.error("Failed to delete payment");
      }
    }
  };

  // Handle notes change - update local state immediately
  const handleNotesChange = (payment, value) => {
    // Update the payment in local state
    setPaymentsData(prevData => 
      prevData.map(p => 
        p.id === payment.id ? { ...p, notes: value } : p
      )
    );
  };

  // Handle notes blur - save to backend when user leaves the field
  const handleNotesBlur = async (payment, value) => {
    if (!payment.guardian_id) {
      console.error('Cannot update notes: guardian_id not found');
      return;
    }

    try {
      // Use guardians changeField API to update notes (matching Laravel js-guardians-change-field)
      // Route: POST /api/guardians/change-field (guardianId in body, not URL)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/guardians/change-field`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify({
          guardianId: payment.guardian_id,
          field: 'payments_notes', // Use payments_notes field (guardian's payments-specific notes)
          value: value
        })
      });

      const data = await response.json();

      if (data.success) {
        // Reload payments to get updated editor info
        if (useServerSide) {
          fetchPaymentsServerSide();
        } else {
          loadPaymentsData();
        }
      } else {
        toast.error(data.message || "Failed to update notes");
        // Revert to original value on error
        setPaymentsData(prevData => 
          prevData.map(p => 
            p.id === payment.id ? { ...p, notes: payment.notes } : p
          )
        );
      }
    } catch (error) {
      console.error('Error updating notes:', error);
      toast.error("Failed to update notes");
      // Revert to original value on error
      setPaymentsData(prevData => 
        prevData.map(p => 
          p.id === payment.id ? { ...p, notes: payment.notes } : p
        )
      );
    }
  };

  const formatCurrency = (amount) => {
    return `£${Number(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getOAStatusBadge = (oaValue) => {
    // O/A column now shows outstanding amount as currency (matching Laravel)
    // oaValue can be a number (outstanding amount) or string ('O'/'A' for backward compatibility)
    if (typeof oaValue === 'number') {
      // Display as currency (matching Laravel: £454, £-229.4)
      return formatCurrency(oaValue);
    } else if (oaValue === 'O') {
      // Backward compatibility: show 'O' badge if old format
      return <span className={`${styles.oaBadge} ${styles.occupied}`}>O</span>;
    } else if (oaValue === 'A') {
      // Backward compatibility: show 'A' badge if old format
      return <span className={`${styles.oaBadge} ${styles.absent}`}>A</span>;
    }
    // Default: show 0 if no value
    return formatCurrency(0);
  };

  // Laravel payment status badge configuration (config/rents.php)
  // Laravel payment_status_label mapping:
  // 0 => 'danger' (Not paid - RED), 1 => 'success' (Paid - GREEN), 2 => 'warning' (To pay - YELLOW)
  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      '0': { label: 'danger', text: 'Not paid', color: '#d9534f' }, // RED - Missed periods should be red
      '1': { label: 'success', text: 'Paid', color: '#5cb85c' },
      '2': { label: 'warning', text: 'To pay', color: '#f0ad4e' }, // YELLOW - To Pay (December) should be yellow
      '3': { label: 'default', text: 'Ignore', color: '#777' },
      '4': { label: 'info', text: 'Payment Plan', color: '#5bc0de' },
      '5': { label: 'danger', text: 'Legal', color: '#d9534f' },
      '6': { label: 'danger', text: 'Notice to quit', color: '#d9534f' },
      'not_paid': { label: 'danger', text: 'Not paid', color: '#d9534f' }, // RED
      'not paid': { label: 'danger', text: 'Not paid', color: '#d9534f' }, // RED - Backend sends with space
      'paid': { label: 'success', text: 'Paid', color: '#5cb85c' },
      'pending': { label: 'warning', text: 'Pending', color: '#f0ad4e' },
      'to pay': { label: 'warning', text: 'To pay', color: '#f0ad4e' }, // YELLOW - Backend sends with space
      'overdue': { label: 'danger', text: 'Overdue', color: '#d9534f' } // RED
    };

    const statusLower = typeof status === 'string' ? status.toLowerCase() : String(status);
    const config = statusConfig[statusLower] || statusConfig[String(status)] ||
                   { label: 'default', text: status, color: '#777' };

    return (
      <span
        className={`${styles.paymentBadge} ${styles[config.label]}`}
        style={{
          backgroundColor: config.color,
          color: 'white',
          padding: "2px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        {config.text}
      </span>
    );
  };

  const getRentStatusBadge = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'active') {
      return <span className={`${styles.rentBadge} ${styles.active}`}>Active</span>;
    } else if (statusLower === 'inactive') {
      return <span className={`${styles.rentBadge} ${styles.inactive}`}>Inactive</span>;
    }
    return <span className={styles.rentBadge}>{status}</span>;
  };

  if (initialLoading) {
    return (
      <div className={styles.paymentsContainer}>
        <div className={styles.loading}>Loading payments data...</div>
      </div>
    );
  }

  if (!paymentsData) {
    return (
      <div className={styles.paymentsContainer}>
        <div className={styles.error}>Failed to load payments data</div>
      </div>
    );
  }



  return (
    <div className={styles.paymentsContainer}>
      {/* Debug Toggle */}
      <div className={styles.debugToggle}>
        <label>
          <input 
            type="checkbox" 
            checked={useMockData} 
            onChange={toggleMockData}
          />
          Use Mock Data
        </label>
      </div>

      {/* Breadcrumb */}
      <div className={styles.breadcrumb}>
        <span onClick={() => navigate('/dashboard')}>Dashboard</span> / Payments
      </div>

      {/* Activity Overview Section */}
      <div className={styles.activityOverviewSection}>
        <h3 className={styles.sectionTitle}>Activity Overview</h3>
        <div className={styles.activityGrid}>
          <div
            className={`${styles.activityCard} ${styles.allCard} ${activeFilter === 'all' ? styles.active : ''}`}
            onClick={() => handleFilterClick('all')}
          >
            <div className={styles.activityNumber}>{statistics.all}</div>
            <div className={styles.activityLabel}>ALL</div>
          </div>
          <div
            className={`${styles.activityCard} ${styles.overdueCard} ${activeFilter === 'overdue' ? styles.active : ''}`}
            onClick={() => handleFilterClick('overdue')}
          >
            <div className={styles.activityNumber}>{statistics.overdue}</div>
            <div className={styles.activityLabel}>OVERDUE</div>
          </div>
          <div
            className={`${styles.activityCard} ${styles.paidCard} ${activeFilter === 'paid' ? styles.active : ''}`}
            onClick={() => handleFilterClick('paid')}
          >
            <div className={styles.activityNumber}>{statistics.paid}</div>
            <div className={styles.activityLabel}>PAID</div>
          </div>
          <div
            className={`${styles.activityCard} ${styles.toPayCard} ${activeFilter === 'to_pay' ? styles.active : ''}`}
            onClick={() => handleFilterClick('to_pay')}
          >
            <div className={styles.activityNumber}>{statistics.to_pay}</div>
            <div className={styles.activityLabel}>TO PAY</div>
          </div>
          <div 
            className={`${styles.activityCard} ${styles.legalCard} ${activeFilter === 'legal' ? styles.active : ''}`}
            onClick={() => handleFilterClick('legal')}
          >
            <div className={styles.activityNumber}>{statistics.legal}</div>
            <div className={styles.activityLabel}>LEGAL</div>
          </div>
          <div 
            className={`${styles.activityCard} ${styles.paymentPlanCard} ${activeFilter === 'payment_plan' ? styles.active : ''}`}
            onClick={() => handleFilterClick('payment_plan')}
          >
            <div className={styles.activityNumber}>{statistics.payment_plan}</div>
            <div className={styles.activityLabel}>PAYMENT PLAN</div>
          </div>
          <div 
            className={`${styles.activityCard} ${styles.noticeCard} ${activeFilter === 'notice_to_quit' ? styles.active : ''}`}
            onClick={() => handleFilterClick('notice_to_quit')}
          >
            <div className={styles.activityNumber}>{statistics.notice_to_quit}</div>
            <div className={styles.activityLabel}>NOTICE TO QUIT</div>
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className={styles.statisticsSection}>
        <div className={styles.statisticsGrid}>
          <div className={`${styles.statisticsCard} ${styles.statisticsRed}`}>
            <div className={styles.statisticsLabel}>THE TOTAL PRICE OF OCCUPIED SPACE</div>
            <div className={styles.statisticsAmount}>{formatCurrency(statistics.totalPriceOfOccupiedRooms)}</div>
          </div>
          <div className={`${styles.statisticsCard} ${styles.statisticsGreen}`}>
            <div className={styles.statisticsLabel}>THE TOTAL PRICE OF VACANT SPACE</div>
            <div className={styles.statisticsAmount}>{formatCurrency(statistics.totalPriceOfVacantRooms)}</div>
          </div>
        </div>
      </div>

      {/* Guardian Payments Section */}
      <div className={styles.guardianPaymentsSection}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Guardian Payments</h3>
          <div className={styles.sectionActions}>
            <button className={styles.btnExport} onClick={handleExportCSV}>
              Export CSV
            </button>
            <button className={styles.btnExport} onClick={handleExportPDF}>
              Export PDF
            </button>
            <button className={styles.btnExport} onClick={handlePrintTable}>
              Print Table
            </button>
          </div>
        </div>

        {/* DataTables Control Bar */}
        <div className={styles.datatableControls}>
          <div className={styles.entriesControl}>
            <label>Show entries:</label>
            <select 
              className={styles.entriesSelect}
              value={entriesPerPage}
              onChange={(e) => {
                const newValue = Number(e.target.value);
                setEntriesPerPage(newValue);
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value={75}>75</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className={styles.searchControl} style={{ position: 'relative' }}>
            <label>Search:</label>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search payments..."
              value={tableSearch}
              onChange={(e) => {
                setTableSearch(e.target.value);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Trigger search only on Enter key press
                  const searchValue = e.target.value;
                  setTableSearch(searchValue); // Update display value
                  setDebouncedSearch(searchValue); // Trigger search
                  setCurrentPage(1); // Reset to page 1 when search is triggered
                }
              }}
              style={{ paddingRight: loading ? '30px' : '12px' }}
            />
            {loading && (
              <div style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '12px',
                color: '#999'
              }}>
                ⏳
              </div>
            )}
          </div>

          <div className={styles.actionButtons}>
            <div className={styles.visibilityBtnContainer}>
              <button 
                className={`${styles.visibilityBtn} visibility-btn`}
                onClick={handleColumnVisibility}
              >
                Column visibility
              </button>
              {showColumnVisibility && (
                <div className={`${styles.columnVisibilityDropdown} column-visibility-dropdown`}>
                  <h4>Toggle Columns:</h4>
                  <label className={styles.columnToggle} onClick={handleCheckboxClick}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.firstName}
                      onChange={() => toggleColumnVisibility('firstName')}
                      onClick={handleCheckboxClick}
                    />
                    First Name
                  </label>
                  <label className={styles.columnToggle} onClick={handleCheckboxClick}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.lastName}
                      onChange={() => toggleColumnVisibility('lastName')}
                      onClick={handleCheckboxClick}
                    />
                    Last Name
                  </label>
                  <label className={styles.columnToggle} onClick={handleCheckboxClick}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.mobile}
                      onChange={() => toggleColumnVisibility('mobile')}
                      onClick={handleCheckboxClick}
                    />
                    Mobile
                  </label>
                  <label className={styles.columnToggle} onClick={handleCheckboxClick}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.fee}
                      onChange={() => toggleColumnVisibility('fee')}
                      onClick={handleCheckboxClick}
                    />
                    Fee
                  </label>
                  <label className={styles.columnToggle} onClick={handleCheckboxClick}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.room}
                      onChange={() => toggleColumnVisibility('room')}
                      onClick={handleCheckboxClick}
                    />
                    Room
                  </label>
                  <label className={styles.columnToggle} onClick={handleCheckboxClick}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.dueDate}
                      onChange={() => toggleColumnVisibility('dueDate')}
                      onClick={handleCheckboxClick}
                    />
                    Due Date
                  </label>
                  <label className={styles.columnToggle} onClick={handleCheckboxClick}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.movedInDate}
                      onChange={() => toggleColumnVisibility('movedInDate')}
                      onClick={handleCheckboxClick}
                    />
                    Moved In Date
                  </label>
                  <label className={styles.columnToggle} onClick={handleCheckboxClick}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.oa}
                      onChange={() => toggleColumnVisibility('oa')}
                      onClick={handleCheckboxClick}
                    />
                    O/A
                  </label>
                  <label className={styles.columnToggle} onClick={handleCheckboxClick}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.paymentStatus}
                      onChange={() => toggleColumnVisibility('paymentStatus')}
                      onClick={handleCheckboxClick}
                    />
                    Payment Status
                  </label>
                  <label className={styles.columnToggle} onClick={handleCheckboxClick}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.rentStatus}
                      onChange={() => toggleColumnVisibility('rentStatus')}
                      onClick={handleCheckboxClick}
                    />
                    Rent Status
                  </label>
                  <label className={styles.columnToggle} onClick={handleCheckboxClick}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.property}
                      onChange={() => toggleColumnVisibility('property')}
                      onClick={handleCheckboxClick}
                    />
                    Property
                  </label>
                  <label className={styles.columnToggle} onClick={handleCheckboxClick}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.notes}
                      onChange={() => toggleColumnVisibility('notes')}
                      onClick={handleCheckboxClick}
                    />
                    Notes
                  </label>
                  <label className={styles.columnToggle} onClick={handleCheckboxClick}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.bankAccount}
                      onChange={() => toggleColumnVisibility('bankAccount')}
                      onClick={handleCheckboxClick}
                    />
                    Bank Account
                  </label>
                  <label className={styles.columnToggle} onClick={handleCheckboxClick}>
                    <input
                      type="checkbox"
                      checked={columnVisibility.actions}
                      onChange={() => toggleColumnVisibility('actions')}
                      onClick={handleCheckboxClick}
                    />
                    Actions
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.resultCount}>
          (Showing {paginatedPayments.length} of {totalRecords} results)
        </div>

        <div className={styles.paymentsTableContainer} style={{ position: 'relative' }}>
          {loading && paymentsData.length > 0 && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10
            }}>
              <div style={{ fontSize: '16px', color: '#333' }}>Loading...</div>
            </div>
          )}
          <table className={styles.paymentsTable} style={{ opacity: loading && paymentsData.length > 0 ? 0.5 : 1 }}>
            <thead>
              <tr>
                <th className={styles.selectColumn}>
                  <input
                    type="checkbox"
                    checked={selectedRows.length > 0 && selectedRows.length === paginatedPayments.length}
                    onChange={handleSelectAll}
                    className={styles.selectAllCheckbox}
                  />
                </th>
                {columnVisibility.firstName && (
                  <th className={styles.sortableHeader} onClick={() => handleSort('first_name')}>
                    First <span className={styles.sortIcon}>{getSortIcon('first_name')}</span>
                  </th>
                )}
                {columnVisibility.lastName && (
                  <th className={styles.sortableHeader} onClick={() => handleSort('last_name')}>
                    Last <span className={styles.sortIcon}>{getSortIcon('last_name')}</span>
                  </th>
                )}
                {columnVisibility.mobile && (
                  <th className={styles.sortableHeader} onClick={() => handleSort('mobile')}>
                    Mobile <span className={styles.sortIcon}>{getSortIcon('mobile')}</span>
                  </th>
                )}
                {columnVisibility.fee && (
                  <th className={styles.sortableHeader} onClick={() => handleSort('fee')}>
                    Fee <span className={styles.sortIcon}>{getSortIcon('fee')}</span>
                  </th>
                )}
                {columnVisibility.room && (
                  <th className={styles.sortableHeader} onClick={() => handleSort('room')}>
                    Room <span className={styles.sortIcon}>{getSortIcon('room')}</span>
                  </th>
                )}
                {columnVisibility.dueDate && (
                  <th className={styles.sortableHeader} onClick={() => handleSort('due_date')}>
                    Due Date <span className={styles.sortIcon}>{getSortIcon('due_date')}</span>
                  </th>
                )}
                {columnVisibility.movedInDate && (
                  <th className={styles.sortableHeader} onClick={() => handleSort('moved_in_date')}>
                    Moved in Date <span className={styles.sortIcon}>{getSortIcon('moved_in_date')}</span>
                  </th>
                )}
                {columnVisibility.oa && (
                  <th className={styles.sortableHeader} onClick={() => handleSort('oa_status')}>
                    O/A <span className={styles.sortIcon}>{getSortIcon('oa_status')}</span>
                  </th>
                )}
                {columnVisibility.paymentStatus && (
                  <th className={styles.sortableHeader} onClick={() => handleSort('payment_status')}>
                    Payment Status <span className={styles.sortIcon}>{getSortIcon('payment_status')}</span>
                  </th>
                )}
                {columnVisibility.rentStatus && (
                  <th className={styles.sortableHeader} onClick={() => handleSort('rent_status')}>
                    Rent Status <span className={styles.sortIcon}>{getSortIcon('rent_status')}</span>
                  </th>
                )}
                {columnVisibility.property && (
                  <th className={styles.sortableHeader} onClick={() => handleSort('property')}>
                    Property <span className={styles.sortIcon}>{getSortIcon('property')}</span>
                  </th>
                )}
                {columnVisibility.notes && (
                  <th className={styles.sortableHeader} onClick={() => handleSort('notes')}>
                    Notes (Textarea) <span className={styles.sortIcon}>{getSortIcon('notes')}</span>
                  </th>
                )}
                {columnVisibility.bankAccount && (
                  <th className={styles.sortableHeader} onClick={() => handleSort('bank_account')}>
                    Bank Account <span className={styles.sortIcon}>{getSortIcon('bank_account')}</span>
                  </th>
                )}
                {columnVisibility.actions && <th className={styles.actionHead}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedPayments.length > 0 ? (
                paginatedPayments.map((payment, index) => {
                  const isSelected = selectedRows.includes(payment.id);
                  return (
                    <tr
                      key={payment.id}
                      className={`
                        ${index % 2 === 0 ? styles.evenRow : styles.oddRow}
                        ${isSelected ? styles.selectedRow : ''}
                        ${styles.clickableRow}
                      `}
                      onClick={(e) => handleRowClick(payment, e)}
                    >
                      <td className={styles.selectCell}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleRowClick(payment, { target: { closest: () => null } })}
                          onClick={(e) => e.stopPropagation()}
                          className={styles.rowSelectCheckbox}
                        />
                      </td>
                      {columnVisibility.firstName && <td>{payment.first_name}</td>}
                      {columnVisibility.lastName && <td>{payment.last_name}</td>}
                      {columnVisibility.mobile && <td>{payment.mobile}</td>}
                      {columnVisibility.fee && <td>{formatCurrency(payment.fee)}</td>}
                      {columnVisibility.room && <td>{payment.room}</td>}
                      {columnVisibility.dueDate && <td>{formatDate(payment.due_date)}</td>}
                      {columnVisibility.movedInDate && <td>{formatDate(payment.moved_in_date)}</td>}
                      {columnVisibility.oa && (
                        <td>
                          {getOAStatusBadge(
                            payment.outstanding_amount !== undefined
                              ? payment.outstanding_amount
                              : payment.oa_status
                          )}
                        </td>
                      )}
                      {columnVisibility.paymentStatus && (
                        <td>
                          {(() => {
                            // Laravel: Check if payment is overdue (status 0 + past due date)
                            const paymentStatus = payment.status !== undefined ? payment.status : payment.payment_status;
                            const isOverdue = isPaymentOverdue(payment) && paymentStatus !== '1' && paymentStatus !== 1;
                            return getPaymentStatusBadge(isOverdue ? 'overdue' : paymentStatus);
                          })()}
                        </td>
                      )}
                      {columnVisibility.rentStatus && <td>{getRentStatusBadge(payment.rent_status)}</td>}
                      {columnVisibility.property && <td>{payment.property}</td>}
                      {columnVisibility.notes && (
                        <td className={styles.notesCell}>
                          <textarea
                            value={payment.notes || ''}
                            onChange={(e) => handleNotesChange(payment, e.target.value)}
                            onBlur={(e) => handleNotesBlur(payment, e.target.value)}
                            className={styles.notesTextarea}
                            rows={3}
                            placeholder="Enter notes..."
                          />
                          {payment.notes_edited_by && payment.notes_edited_at && (
                            <span className="pull-left" style={{ fontSize: 'smaller', fontStyle: 'italic' }}>
                              Last edited by {payment.notes_editor_name || 'Unknown'} at {new Date(payment.notes_edited_at).toLocaleString()}
                            </span>
                          )}
                        </td>
                      )}
                      {columnVisibility.bankAccount && <td>{payment.bank_account}</td>}
                      {columnVisibility.actions && (
                        <td className={styles.actionsColumn}>
                          <div className={styles.actionButtonsGroup}>
                        {/* Details button - Laravel: @if (in_array('payments.list', $globalPermissions)) */}
                        {hasPermission('payments.list') && (
                          <button
                                className={`${styles.actionBtn} ${styles.btnBlue}`}
                            // title="Details - Guardian Payments"
                            onClick={() => {
                              // Navigate to guardian payments based on payment data
                              if (payment.guardian_id) {
                                navigate(`/guardians/${payment.guardian_id}/payments`);
                              } else {
                                // Fallback - extract guardian ID from guardian name or use mock ID
                                const fallbackGuardianId = 66631; // Using your example ID
                                navigate(`/guardians/${fallbackGuardianId}/payments`);
                              }
                            }}
                          >Details</button>
                        )}

                        {/* Property Payments button - Laravel: @if (in_array('payments.list', $globalPermissions)) */}
                        {hasPermission('payments.list') && (
                          <button
                                className={`${styles.actionBtn} ${styles.btnGreen}`}
                            // title="Property Payments"
                            onClick={() => {
                              // Navigate to property payments based on payment data
                              if (payment.property_id) {
                                navigate(`/properties/${payment.property_id}/payments`);
                              } else {
                                // Fallback - extract property ID from property name or use mock ID
                                const fallbackPropertyId = 2218; // Using your example ID
                                navigate(`/properties/${fallbackPropertyId}/payments`);
                              }
                            }}
                          >Property Payments</button>
                        )}

                        {/* Send Reminder button - Laravel: @if (in_array('payments.manage', $globalPermissions)) @if($model->isNotPaid() && $model->isOverdue()) */}
                        {/* NOTE: In Laravel buttons_active.blade.php, only Details, Property Payments, and Send Reminder are shown */}
                        {/* Mark as Paid, Edit, and Remove buttons are NOT shown on the main payments index page */}
                        {hasPermission('payments.manage') && isPaymentNotPaid(payment) && isPaymentOverdue(payment) && (
                          <button
                                className={`${styles.actionBtn} ${styles.btnCyan}`}
                            title="Send Reminder"
                            onClick={() => handleSendReminder(payment)}
                          >Send Reminder</button>
                        )}
                      </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={Object.values(columnVisibility).filter(Boolean).length + 1} className={styles.noDataCell}>
                    {loading ? 'Loading...' : 'No payments found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination (Users.jsx style with DataTables) */}
        {totalPages > 1 && (
          <div className="dataTables_info_wrapper" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
            <div className="dataTables_info">
              Showing {totalRecords === 0 ? 0 : ((currentPage - 1) * entriesPerPage) + 1} to{" "}
              {Math.min(currentPage * entriesPerPage, totalRecords)} of {totalRecords} entries
            </div>
            <div className="dataTables_paginate">
              <ul className="pagination">
                <li className={currentPage === 1 ? 'disabled' : ''}>
                  <a href="#" onClick={(e) => { e.preventDefault(); if (currentPage > 1) goToPage(currentPage - 1); }}>
                    Previous
                  </a>
                </li>
                {(() => {
                  const pages = [];
                  const maxPagesToShow = 5;
                  const halfRange = Math.floor(maxPagesToShow / 2);

                  let startPage = Math.max(1, currentPage - halfRange);
                  let endPage = Math.min(totalPages, currentPage + halfRange);

                  if (currentPage <= halfRange) {
                    endPage = Math.min(maxPagesToShow, totalPages);
                  }
                  if (currentPage + halfRange >= totalPages) {
                    startPage = Math.max(1, totalPages - maxPagesToShow + 1);
                  }

                  // Add first page and ellipsis if needed
                  if (startPage > 1) {
                    pages.push(
                      <li key={1} className={currentPage === 1 ? 'active' : ''}>
                        <a href="#" onClick={(e) => { e.preventDefault(); goToPage(1); }}>1</a>
                      </li>
                    );
                    if (startPage > 2) {
                      pages.push(<li key="ellipsis-start" className="disabled"><span>...</span></li>);
                    }
                  }

                  // Add page numbers in range
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <li key={i} className={currentPage === i ? 'active' : ''}>
                        <a href="#" onClick={(e) => { e.preventDefault(); goToPage(i); }}>{i}</a>
                      </li>
                    );
                  }

                  // Add ellipsis and last page if needed
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(<li key="ellipsis-end" className="disabled"><span>...</span></li>);
                    }
                    pages.push(
                      <li key={totalPages} className={currentPage === totalPages ? 'active' : ''}>
                        <a href="#" onClick={(e) => { e.preventDefault(); goToPage(totalPages); }}>{totalPages}</a>
                      </li>
                    );
                  }

                  return pages;
                })()}
                <li className={currentPage === totalPages ? 'disabled' : ''}>
                  <a href="#" onClick={(e) => { e.preventDefault(); if (currentPage < totalPages) goToPage(currentPage + 1); }}>
                    Next
                  </a>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Footer Notice */}
      <div className={styles.footerNotice}>
        <div className={styles.noticeText}>
          You have {paginatedPayments.filter(p => p.payment_status === 'Overdue').length} overdue payments that require immediate attention.
        </div>
        <button className={styles.noticeButton} onClick={() => handleFilterClick('overdue')}>
          View Overdue
        </button>
        <button className={styles.noticeClose} onClick={(e) => e.target.parentElement.style.display = 'none'}>
          ×
        </button>
      </div>

      {useMockData && (
        <div className={styles.demoMode}>
          <strong>Demo Mode:</strong> This is running with mock data. Toggle the checkbox in the top-right corner to test with real API.
        </div>
      )}
    </div>
  );
};

export default PaymentsIndex; 